import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { useToast } from "../../hooks/use-toast";
import { 
  MessageSquare, 
  Bot, 
  Users, 
  Settings, 
  Plus, 
  Eye,
  Phone,
  Globe,
  UserPlus,
  Brain,
  Zap,
  Clock,
  CheckCircle
} from "lucide-react";

import { mockApi } from "../../mock";

const ChatManagement = () => {
  const { toast } = useToast();
  const [activeChats, setActiveChats] = useState([]);

  // Load real chat sessions from backend on mount
  useEffect(() => {
    mockApi.listChatSessions().then(sessions => {
      const normalized = (sessions || []).map(s => ({
        id: s.id,
        customerName: s.customerName || s.customerId || 'Customer',
        resellerId: s.resellerId,
        resellerName: s.resellerName || (s.resellerId ? 'Reseller' : 'AI Support'),
        channel: s.channel || 'website',
        status: s.status || 'active',
        lastMessage: s.lastMessage || '(no messages yet)',
        timestamp: s.lastMessageAt || s.createdAt,
        aiAssisted: s.aiAssisted !== false
      }));
      setActiveChats(normalized);
    });
  }, []);

  const [supportTeam, setSupportTeam] = useState([]);
  const [editingMember, setEditingMember] = useState(null);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);

  // Load real support team
  useEffect(() => {
    mockApi.listSupportTeam().then(list => setSupportTeam(list || []));
  }, []);

  const [aiSettings, setAISettings] = useState({
    enabled: true,
    autoRespond: true,
    learningMode: true,
    confidenceThreshold: 0.85,
    fallbackToHuman: true,
    responseDelay: 2000,
    supportedLanguages: ["en", "es"],
    _placeholderKB: true,
    knowledgeBase: {
      businessCards: 95,
      payments: 87,
      mlm: 92,
      technical: 78
    }
  });

  const [newTeamMember, setNewTeamMember] = useState({
    name: "",
    email: "",
    phone: "",
    username: "",
    password: "",
    permissions: []
  });

  const [aiTrainingData, setAITrainingData] = useState([]);
  const [newTraining, setNewTraining] = useState({ question: '', answer: '', category: 'businessCards' });
  const [trainingDialogOpen, setTrainingDialogOpen] = useState(false);

  // Load real AI settings + training data
  useEffect(() => {
    mockApi.fetchAISettings().then(s => { if (s) setAISettings(prev => ({ ...prev, ...s, _placeholderKB: false })); });
    mockApi.fetchAITraining().then(list => setAITrainingData(list || []));
  }, []);

  const reloadSupportTeam = async () => {
    const list = await mockApi.listSupportTeam();
    setSupportTeam(list || []);
  };

  const handleCreateTeamMember = async () => {
    if (!newTeamMember.name || !newTeamMember.email || !newTeamMember.username) {
      toast({ title: "Missing Information", description: "Fill name, email, username", variant: "destructive" });
      return;
    }
    const r = await mockApi.createSupportMember(newTeamMember);
    if (r.success) {
      await reloadSupportTeam();
      setNewTeamMember({ name: "", email: "", phone: "", username: "", password: "", permissions: [] });
      setMemberDialogOpen(false);
      toast({ title: "Team Member Created", description: "Saved to backend" });
    } else {
      toast({ title: "Create failed", variant: "destructive" });
    }
  };

  const openEditMember = (member) => {
    setEditingMember(member);
    setNewTeamMember({
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
      username: member.username || '',
      password: '',
      permissions: member.permissions || []
    });
    setMemberDialogOpen(true);
  };

  const handleSaveMember = async () => {
    if (editingMember) {
      const payload = { ...newTeamMember };
      if (!payload.password) delete payload.password;
      const r = await mockApi.updateSupportMember(editingMember.id, payload);
      if (r.success) {
        await reloadSupportTeam();
        toast({ title: "Member updated" });
        setMemberDialogOpen(false);
        setEditingMember(null);
      } else {
        toast({ title: "Update failed", variant: "destructive" });
      }
    } else {
      await handleCreateTeamMember();
    }
  };

  const handleDeleteMember = async (id, name) => {
    if (!window.confirm(`Delete support member "${name}"?`)) return;
    const r = await mockApi.deleteSupportMember(id);
    if (r.success) {
      await reloadSupportTeam();
      toast({ title: "Member deleted" });
    } else {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  const handleAISettingChange = (key, value) => {
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      setAISettings(prev => ({ ...prev, [parent]: { ...prev[parent], [child]: value } }));
    } else {
      setAISettings(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleSaveAISettings = async () => {
    const r = await mockApi.saveAISettings(aiSettings);
    if (r.success) {
      toast({ title: "AI settings saved", description: "Saved to backend" });
    } else {
      toast({ title: "Save failed", variant: "destructive" });
    }
  };

  const handleAddTrainingData = async () => {
    if (!newTraining.question || !newTraining.answer) {
      toast({ title: "Missing fields", description: "Question and answer required", variant: "destructive" });
      return;
    }
    const payload = {
      ...newTraining,
      confidence: 0.9,
      lastUpdated: new Date().toISOString().split('T')[0]
    };
    const r = await mockApi.addAITraining(payload);
    if (r.success) {
      const list = await mockApi.fetchAITraining();
      setAITrainingData(list || []);
      setNewTraining({ question: '', answer: '', category: 'businessCards' });
      setTrainingDialogOpen(false);
      toast({ title: "Training data added" });
    } else {
      toast({ title: "Add failed", variant: "destructive" });
    }
  };

  const handleDeleteTraining = async (id) => {
    if (!window.confirm('Delete this training entry?')) return;
    const r = await mockApi.deleteAITraining(id);
    if (r.success) {
      const list = await mockApi.fetchAITraining();
      setAITrainingData(list || []);
      toast({ title: "Training entry deleted" });
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'phone': return <Phone className="h-4 w-4" />;
      case 'website': return <Globe className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <MessageSquare className="h-6 w-6 text-blue-600" />
            <span>AI Chat Support Management</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="live-chats" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="live-chats">Live Chats</TabsTrigger>
              <TabsTrigger value="ai-settings">AI Configuration</TabsTrigger>
              <TabsTrigger value="team">Support Team</TabsTrigger>
              <TabsTrigger value="training">AI Training</TabsTrigger>
            </TabsList>

            {/* Live Chats Tab */}
            <TabsContent value="live-chats" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Active Chat Sessions</h3>
                <div className="flex items-center space-x-2">
                  <Badge className="bg-green-100 text-green-800">
                    {activeChats.filter(chat => chat.status === 'active').length} Active
                  </Badge>
                  <Badge className="bg-yellow-100 text-yellow-800">
                    {activeChats.filter(chat => chat.status === 'pending').length} Pending
                  </Badge>
                </div>
              </div>

              <div className="space-y-3">
                {activeChats.map((chat) => (
                  <div key={chat.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        {getChannelIcon(chat.channel)}
                        <Badge className={getStatusColor(chat.status)}>
                          {chat.status}
                        </Badge>
                      </div>
                      <div>
                        <h4 className="font-semibold">{chat.customerName}</h4>
                        <p className="text-sm text-gray-600">{chat.lastMessage}</p>
                        <div className="flex items-center space-x-3 text-xs text-gray-500 mt-1">
                          <span>Agent: {chat.resellerName}</span>
                          <span>•</span>
                          <span>{new Date(chat.timestamp).toLocaleTimeString()}</span>
                          {chat.aiAssisted && (
                            <>
                              <span>•</span>
                              <div className="flex items-center space-x-1">
                                <Bot className="h-3 w-3" />
                                <span>AI Assisted</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button size="sm" variant="outline">
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      {chat.status === 'active' && (
                        <Button size="sm">Join Chat</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* AI Configuration Tab */}
            <TabsContent value="ai-settings" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center space-x-2">
                      <Bot className="h-5 w-5 text-purple-600" />
                      <span>AI Assistant Settings</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Enable AI Chat Support</Label>
                      <Switch
                        checked={aiSettings.enabled}
                        onCheckedChange={(checked) => handleAISettingChange("enabled", checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Auto-Respond to Customer Queries</Label>
                      <Switch
                        checked={aiSettings.autoRespond}
                        onCheckedChange={(checked) => handleAISettingChange("autoRespond", checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Learning Mode (Improve from Conversations)</Label>
                      <Switch
                        checked={aiSettings.learningMode}
                        onCheckedChange={(checked) => handleAISettingChange("learningMode", checked)}
                      />
                    </div>

                    <div>
                      <Label>Confidence Threshold</Label>
                      <Input
                        type="number"
                        min="0.5"
                        max="1"
                        step="0.05"
                        value={aiSettings.confidenceThreshold}
                        onChange={(e) => handleAISettingChange("confidenceThreshold", parseFloat(e.target.value))}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">AI will respond only if confidence is above this threshold</p>
                    </div>

                    <div>
                      <Label>Response Delay (ms)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="5000"
                        step="100"
                        value={aiSettings.responseDelay}
                        onChange={(e) => handleAISettingChange("responseDelay", parseInt(e.target.value))}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Simulate natural response timing</p>
                    </div>
                    <div className="pt-2">
                      <Button onClick={handleSaveAISettings} className="w-full bg-gradient-to-r from-emerald-500 to-blue-600">
                        Save AI Settings
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center space-x-2">
                      <Brain className="h-5 w-5 text-green-600" />
                      <span>Knowledge Base Status</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {Object.entries(aiSettings.knowledgeBase || {}).map(([category, percentage]) => (
                      <div key={category}>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm font-medium capitalize">{category}</span>
                          <span className="text-sm font-medium">{percentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              percentage > 90 ? 'bg-green-600' : 
                              percentage > 80 ? 'bg-yellow-600' : 'bg-red-600'
                            }`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}

                    <div className="pt-4 border-t">
                      <div className="flex items-center space-x-2 mb-2">
                        <Globe className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium">Supported Languages</span>
                      </div>
                      <div className="flex space-x-2">
                        {(aiSettings.supportedLanguages || []).map((lang) => (
                          <Badge key={lang} variant="outline">
                            {lang.toUpperCase()}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Support Team Tab */}
            <TabsContent value="team" className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Support Team Management</h3>
                <Dialog open={memberDialogOpen} onOpenChange={(open) => { setMemberDialogOpen(open); if (!open) { setEditingMember(null); setNewTeamMember({ name: '', email: '', phone: '', username: '', password: '', permissions: [] }); } }}>
                  <DialogTrigger asChild>
                    <Button className="flex items-center space-x-2" onClick={() => { setEditingMember(null); setNewTeamMember({ name: '', email: '', phone: '', username: '', password: '', permissions: [] }); }}>
                      <UserPlus className="h-4 w-4" />
                      <span>Add Team Member</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>{editingMember ? 'Edit Support Team Member' : 'Create Support Team Member'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Full Name *</Label>
                          <Input
                            value={newTeamMember.name}
                            onChange={(e) => setNewTeamMember(prev => ({...prev, name: e.target.value}))}
                            placeholder="John Smith"
                          />
                        </div>
                        <div>
                          <Label>Email *</Label>
                          <Input
                            type="email"
                            value={newTeamMember.email}
                            onChange={(e) => setNewTeamMember(prev => ({...prev, email: e.target.value}))}
                            placeholder="john@cardmaster.com"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Username *</Label>
                          <Input
                            value={newTeamMember.username}
                            onChange={(e) => setNewTeamMember(prev => ({...prev, username: e.target.value}))}
                            placeholder="support_agent_3"
                          />
                        </div>
                        <div>
                          <Label>Phone Number</Label>
                          <Input
                            value={newTeamMember.phone}
                            onChange={(e) => setNewTeamMember(prev => ({...prev, phone: e.target.value}))}
                            placeholder="+1-555-0123"
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Temporary Password *</Label>
                        <Input
                          type="password"
                          value={newTeamMember.password}
                          onChange={(e) => setNewTeamMember(prev => ({...prev, password: e.target.value}))}
                          placeholder="Temporary password"
                        />
                      </div>

                      <div>
                        <Label>Permissions</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {['website_chat', 'phone_support', 'ai_training', 'admin_access'].map((permission) => (
                            <label key={permission} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={newTeamMember.permissions.includes(permission)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setNewTeamMember(prev => ({
                                      ...prev,
                                      permissions: [...prev.permissions, permission]
                                    }));
                                  } else {
                                    setNewTeamMember(prev => ({
                                      ...prev,
                                      permissions: prev.permissions.filter(p => p !== permission)
                                    }));
                                  }
                                }}
                              />
                              <span className="text-sm capitalize">{permission.replace('_', ' ')}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="flex justify-end space-x-3">
                        <Button variant="outline" onClick={() => { setMemberDialogOpen(false); setEditingMember(null); }}>Cancel</Button>
                        <Button onClick={handleSaveMember}>{editingMember ? 'Save changes' : 'Create Team Member'}</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="space-y-4">
                {supportTeam.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                        {(member.name || '?').split(' ').filter(Boolean).map(n => n[0] || '').join('').toUpperCase() || '?'}
                      </div>
                      <div>
                        <h4 className="font-semibold">{member.name}</h4>
                        <p className="text-sm text-gray-600">{member.email}</p>
                        <div className="flex items-center space-x-3 text-xs text-gray-500 mt-1">
                          <span>@{member.username}</span>
                          <span>•</span>
                          <span>{member.activeChats} active chats</span>
                          <span>•</span>
                          <span>Joined {member.createdAt}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Badge className={member.status === 'online' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {member.status || 'offline'}
                      </Badge>
                      <div className="flex space-x-1">
                        {(member.permissions || []).map((permission) => (
                          <Badge key={permission} variant="outline" className="text-xs">
                            {(permission || '').split('_')[0]}
                          </Badge>
                        ))}
                      </div>
                      <Button size="sm" variant="outline" onClick={() => openEditMember(member)}>Edit</Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteMember(member.id, member.name)}>Delete</Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* AI Training Tab */}
            <TabsContent value="training" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center space-x-2">
                    <Zap className="h-5 w-5 text-yellow-600" />
                    <span>AI Training & Knowledge Base</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold">Training Data</h4>
                      <Dialog open={trainingDialogOpen} onOpenChange={setTrainingDialogOpen}>
                        <DialogTrigger asChild>
                          <Button size="sm">
                            <Plus className="h-4 w-4 mr-1" />
                            Add Training Data
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                          <DialogHeader>
                            <DialogTitle>Add AI Training Entry</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-3 py-2">
                            <div>
                              <Label>Question</Label>
                              <Input value={newTraining.question} onChange={(e) => setNewTraining({ ...newTraining, question: e.target.value })} placeholder="What's the question customers ask?" />
                            </div>
                            <div>
                              <Label>Answer</Label>
                              <Textarea value={newTraining.answer} onChange={(e) => setNewTraining({ ...newTraining, answer: e.target.value })} placeholder="The answer AI should give" />
                            </div>
                            <div>
                              <Label>Category</Label>
                              <Select value={newTraining.category} onValueChange={(v) => setNewTraining({ ...newTraining, category: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="businessCards">Business Cards</SelectItem>
                                  <SelectItem value="templates">Templates</SelectItem>
                                  <SelectItem value="pricing">Pricing</SelectItem>
                                  <SelectItem value="mlmSystem">MLM System</SelectItem>
                                  <SelectItem value="technicalSupport">Technical Support</SelectItem>
                                  <SelectItem value="ordering">Ordering</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="flex justify-end space-x-2">
                            <Button variant="outline" onClick={() => setTrainingDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleAddTrainingData}>Add</Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>

                    <div className="space-y-3">
                      {aiTrainingData.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-6">No AI training data yet. Click "Add Training Data" above.</p>
                      )}
                      {aiTrainingData.map((data) => (
                        <div key={data.id} className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <h5 className="font-medium text-sm">{data.question}</h5>
                              <p className="text-sm text-gray-600 mt-1">{data.answer}</p>
                            </div>
                            <div className="flex items-center space-x-2 ml-4">
                              <Badge variant="outline">{data.category || 'general'}</Badge>
                              <div className="flex items-center space-x-1">
                                <div className={`w-2 h-2 rounded-full ${
                                  (data.confidence ?? 0) > 0.9 ? 'bg-green-500' :
                                  (data.confidence ?? 0) > 0.8 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}></div>
                                <span className="text-xs text-gray-500">{Math.round((data.confidence ?? 0) * 100)}%</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>Last updated: {data.lastUpdated || '-'}</span>
                            <div className="space-x-2">
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-red-600" onClick={() => handleDeleteTraining(data.id)}>Delete</Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">AI Training Tips</h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Add common customer questions and detailed answers</li>
                        <li>• Include product-specific information and troubleshooting</li>
                        <li>• Update training data based on actual customer conversations</li>
                        <li>• Review and improve low-confidence responses regularly</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChatManagement;